import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import ActionBar from '../components/ActionBar';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Search, CalendarDays, Users, CheckCircle2, XCircle, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const STATUS_INFO = {
  tr: {
    confirmed: { label: '✅ Onaylandı', bg: '#dcfce7', color: '#166534' },
    pending_payment: { label: '⏳ Ödeme Bekleniyor', bg: '#fef3c7', color: '#92400e' },
    checked_in: { label: '🏨 Check-in Yapıldı', bg: '#dbeafe', color: '#1e40af' },
    checked_out: { label: '✓ Check-out Yapıldı', bg: '#f3f4f6', color: '#374151' },
    cancelled: { label: '❌ İptal Edildi', bg: '#fee2e2', color: '#991b1b' },
  },
  en: {
    confirmed: { label: '✅ Confirmed', bg: '#dcfce7', color: '#166534' },
    pending_payment: { label: '⏳ Pending Payment', bg: '#fef3c7', color: '#92400e' },
    checked_in: { label: '🏨 Checked In', bg: '#dbeafe', color: '#1e40af' },
    checked_out: { label: '✓ Checked Out', bg: '#f3f4f6', color: '#374151' },
    cancelled: { label: '❌ Cancelled', bg: '#fee2e2', color: '#991b1b' },
  }
};

export default function BookingLookupPage() {
  const { language, formatPrice, BACKEND_URL, t, getImageUrl } = useApp();
  const navigate = useNavigate();
  const [form, setForm] = useState({ booking_ref: '', email: '' });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  // Set page title
  useEffect(() => {
    document.title = language === 'tr'
      ? 'Rezervasyonumu Sorgula | Kastamonu Amorium Hotel | 4 Mevsim Doğa Tatili'
      : 'My Booking | Kastamonu Amorium Hotel | 4 Seasons Nature Holiday';
  }, [language]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!form.booking_ref.trim() || !form.email.trim()) {
      toast.error(language === 'tr' ? 'Lütfen her iki alanı da doldurun' : 'Please fill both fields');
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/reservations/lookup`, {
        booking_ref: form.booking_ref.trim().toUpperCase(),
        email: form.email.trim(),
      });
      setResult(res.data);
    } catch (err) {
      setResult(null);
      if (err.response?.status === 404) {
        toast.error(language === 'tr' ? 'Rezervasyon bulunamadı. Referans kodunu ve e-postayı kontrol edin.' : 'Reservation not found. Check your reference and email.');
      } else {
        toast.error(language === 'tr' ? 'Bir hata oluştu' : 'An error occurred');
      }
    }
    setLoading(false);
  };

  const nights = result ? Math.round((new Date(result.check_out) - new Date(result.check_in)) / (1000 * 60 * 60 * 24)) : 0;
  const statusInfo = result ? (STATUS_INFO[language]?.[result.status] || STATUS_INFO['tr'][result.status] || { label: result.status, bg: '#f3f4f6', color: '#374151' }) : null;

  const formatDate = (d) => new Date(d).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div style={{ minHeight: '100vh', background: '#F6F1E7', fontFamily: 'Manrope, sans-serif' }}>
      <ActionBar />
      <Navbar />
      <div style={{ maxWidth: '560px', margin: '0 auto', padding: '3rem 1.5rem 4rem' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ width: '56px', height: '56px', background: '#1F3A2E', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
            <Search size={24} style={{ color: '#B08D57' }} />
          </div>
          <h1 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '30px', fontWeight: 600, color: '#1F3A2E', marginBottom: '6px' }}>
            {language === 'tr' ? 'Rezervasyonumu Sorgula' : 'Look Up My Booking'}
          </h1>
          <p style={{ fontSize: '14px', color: '#6E7F5B' }}>
            {language === 'tr'
              ? 'Rezervasyon referans kodunuz ve e-posta adresinizle rezervasyonunuzu görüntüleyin.'
              : 'View your booking details with your reference code and email address.'}
          </p>
        </div>

        {/* Search Form */}
        <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #EFE7DA', padding: '24px', marginBottom: '24px' }}>
          <form onSubmit={handleSearch}>
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#6E7F5B', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '5px' }}>
                {language === 'tr' ? 'Rezervasyon Referansı' : 'Booking Reference'}
              </label>
              <input
                value={form.booking_ref}
                onChange={e => setForm(f => ({ ...f, booking_ref: e.target.value.toUpperCase() }))}
                placeholder="AMR-XXXXXXXX"
                style={{ width: '100%', padding: '10px 12px', fontSize: '16px', fontFamily: 'monospace', fontWeight: 700, border: '1.5px solid #D8D1C6', borderRadius: '10px', outline: 'none', boxSizing: 'border-box', letterSpacing: '0.05em', textTransform: 'uppercase' }}
                onFocus={e => e.target.style.borderColor = '#1F3A2E'}
                onBlur={e => e.target.style.borderColor = '#D8D1C6'}
              />
            </div>
            <div style={{ marginBottom: '18px' }}>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#6E7F5B', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '5px' }}>
                {language === 'tr' ? 'E-posta Adresi' : 'Email Address'}
              </label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder={language === 'tr' ? 'Rezervasyonda kullandığınız e-posta' : 'Email used during booking'}
                style={{ width: '100%', padding: '10px 12px', fontSize: '14px', border: '1.5px solid #D8D1C6', borderRadius: '10px', outline: 'none', boxSizing: 'border-box', fontFamily: 'Manrope, sans-serif' }}
                onFocus={e => e.target.style.borderColor = '#1F3A2E'}
                onBlur={e => e.target.style.borderColor = '#D8D1C6'}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              style={{ width: '100%', background: '#1F3A2E', color: 'white', padding: '13px', borderRadius: '12px', border: 'none', cursor: 'pointer', fontSize: '15px', fontWeight: 700, fontFamily: 'Manrope, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
              onMouseEnter={e => !loading && (e.currentTarget.style.background = '#3E5A3F')}
              onMouseLeave={e => e.currentTarget.style.background = '#1F3A2E'}
            >
              <Search size={16} />
              {loading ? (language === 'tr' ? 'Aranıyor...' : 'Searching...') : (language === 'tr' ? 'Rezervasyonu Sorgula' : 'Find Booking')}
            </button>
          </form>
        </div>

        {/* Result */}
        {searched && !loading && result && (
          <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #EFE7DA', overflow: 'hidden' }}>
            {/* Room image */}
            {result.room_images?.[0] && (
              <div style={{ height: '140px', overflow: 'hidden' }}>
                <img src={getImageUrl(result.room_images[0])} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            )}

            <div style={{ padding: '20px 24px' }}>
              {/* Ref + Status */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
                <code style={{ fontFamily: 'monospace', fontSize: '18px', fontWeight: 800, color: '#1F3A2E', background: '#F6F1E7', padding: '4px 12px', borderRadius: '8px' }}>
                  {result.booking_ref}
                </code>
                {statusInfo && (
                  <span style={{ background: statusInfo.bg, color: statusInfo.color, fontSize: '12px', fontWeight: 700, padding: '5px 14px', borderRadius: '20px' }}>
                    {statusInfo.label}
                  </span>
                )}
              </div>

              {result.room_name && (
                <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '20px', fontWeight: 600, color: '#1F3A2E', marginBottom: '14px' }}>
                  {result.room_name}
                </p>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px', marginBottom: '14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#6E7F5B' }}><CalendarDays size={14} /> {t('check_in_date')}</span>
                  <span style={{ fontWeight: 600, color: '#1F3A2E' }}>{formatDate(result.check_in)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#6E7F5B' }}><CalendarDays size={14} /> {t('check_out_date')}</span>
                  <span style={{ fontWeight: 600, color: '#1F3A2E' }}>{formatDate(result.check_out)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#6E7F5B' }}><Users size={14} /> {t('guests')}</span>
                  <span style={{ fontWeight: 600, color: '#1F3A2E' }}>{result.guests} {t('guest_count')}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6E7F5B' }}>{t('total_nights')}</span>
                  <span style={{ fontWeight: 600, color: '#1F3A2E' }}>{nights}</span>
                </div>
              </div>

              {/* Tax breakdown */}
              {result.subtotal_try > 0 && (
                <div style={{ borderTop: '1px solid #EFE7DA', paddingTop: '10px', marginBottom: '4px', fontSize: '13px', color: '#5A5450' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                    <span>{language === 'tr' ? 'Oda Ücreti (Vergiler Hariç)' : 'Room Rate (Excl. Taxes)'}</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPrice(result.subtotal_try)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                    <span>KDV ({result.vat_rate ?? 10}%)</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPrice(result.vat_amount_try || 0)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                    <span>{language === 'tr' ? 'Konaklama Vergisi' : 'Accommodation Tax'} ({result.accommodation_tax_rate ?? 1}%)</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPrice(result.accommodation_tax_amount_try || 0)}</span>
                  </div>
                </div>
              )}

              <div style={{ borderTop: '1px solid #EFE7DA', paddingTop: '12px', display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: '16px', color: '#1F3A2E', marginBottom: '14px' }}>
                <span>{t('total_price')}</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPrice(result.total_price_try)}</span>
              </div>

              {/* Info box */}
              <div style={{ background: '#F6F1E7', borderRadius: '10px', padding: '12px 14px', fontSize: '13px', color: '#5A5450' }}>
                <p style={{ fontWeight: 700, color: '#1F3A2E', marginBottom: '4px' }}>ℹ️ {language === 'tr' ? 'Bilgi' : 'Information'}</p>
                <p style={{ marginBottom: '2px' }}>📍 Kumara Mah. Tekçam Mevkii No:7, Budamış Köyü / Kastamonu</p>
                <p style={{ marginBottom: '2px' }}>📞 +90 (366) 220 09 09</p>
                <p style={{ marginBottom: '2px' }}>🕐 {language === 'tr' ? 'Giriş: 14:00 | Çıkış: 12:00' : 'Check-in: 14:00 | Check-out: 12:00'}</p>
              </div>
            </div>
          </div>
        )}

        {/* Back to home */}
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <button
            onClick={() => navigate('/')}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#6E7F5B', fontSize: '14px', fontFamily: 'Manrope, sans-serif', textDecoration: 'underline' }}
          >
            {language === 'tr' ? '← Ana Sayfaya Dön' : '← Back to Home'}
          </button>
        </div>
      </div>
      <Footer />
    </div>
  );
}
